import type { FormItemRule, FormRules } from "element-plus";
import type { RegistrationField } from "./userData";

export type RegistrationFieldValidationError =
  | "required"
  | "email"
  | "tel"
  | "date"
  | "regex";

type Translate = (key: string, values?: Record<string, string>) => string;

type FieldProp = (field: RegistrationField) => string;

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === "";
}

function isEmailField(field: RegistrationField): boolean {
  return field.property === "email" || field.inputType === "email";
}

function matchesConfiguredRegex(pattern: string, value: string): boolean {
  try {
    // Tenant settings provide this pattern at runtime.
    // eslint-disable-next-line security/detect-non-literal-regexp
    return new RegExp(pattern).test(value);
  } catch {
    return true;
  }
}

export function isRegistrationFieldEmpty(
  field: RegistrationField,
  value: unknown,
): boolean {
  return isEmpty(value) && !(field.nullable && value === null);
}

export function getRegistrationFieldValidationError(
  field: RegistrationField,
  value: unknown,
): RegistrationFieldValidationError | undefined {
  if (field.mandatory && isRegistrationFieldEmpty(field, value)) {
    return "required";
  }
  if (isEmpty(value)) return undefined;

  const stringValue = String(value);
  if (isEmailField(field) && !/^\S+@\S+\.\S+$/.test(stringValue)) {
    return "email";
  }
  if (field.inputType === "tel" && !/^\+?[0-9]{7,30}$/.test(stringValue)) {
    return "tel";
  }
  if (field.type === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    return "date";
  }
  if (field.regex && !matchesConfiguredRegex(field.regex, stringValue)) {
    return "regex";
  }
  return undefined;
}

export function getRegistrationFieldValidationMessage(
  field: RegistrationField,
  error: RegistrationFieldValidationError,
  t: Translate,
): string {
  switch (error) {
    case "required":
      return t("fieldIsRequired", { field: t(field.label) });
    case "email":
      return t("notAnEmailAdress");
    case "tel":
      return `${t("phoneNumber")}: 1234567 or +1234567`;
    case "regex":
      return t(field.regexDescription ?? field.label);
    case "date":
      return t("genericError");
  }
}

export function createRegistrationFieldRules(
  fields: RegistrationField[],
  t: Translate,
  fieldProp: FieldProp = (field) => field.property,
): FormRules {
  const rules: FormRules = {};

  for (const field of fields) {
    if (
      !field.mandatory &&
      !isEmailField(field) &&
      field.inputType !== "tel" &&
      field.type !== "date" &&
      !field.regex
    ) {
      continue;
    }

    const rule: FormItemRule = {
      validator: (_rule, value, callback) => {
        const error = getRegistrationFieldValidationError(field, value);
        if (error) {
          callback(
            new Error(getRegistrationFieldValidationMessage(field, error, t)),
          );
          return;
        }
        callback();
      },
      trigger: ["blur", "change"],
    };
    rules[fieldProp(field)] = [rule];
  }

  return rules;
}
